import { useCallback, useRef } from 'react';

/**
 * This is a hook which provides a ref and a function to reset
 * the scroll position of the element.
 *
 * @example
 * const MyComponent = () => {
 *   const { ref, onResetScroll } = useResettableScroll<HTMLDivElement>();
 *   return (
 *      <>
 *        <button onClick={onResetScroll}>Reset Scroll</button>
 *        <li ref={ref}>
 *          // Here you would put some content, probably in a height-limited container which scrolls,
 *          // so that you can auto-scroll to the top when the callback is called.
 *        </li>
 *      </>
 *    );
 * };
 */
export default function useResettableScroll<E extends HTMLElement>() {
  const ref = useRef<E>(null);
  const onResetScroll = useCallback(() => {
    if (ref.current) {
      ref.current.scrollTo(0, 0);
    }
  //  Note: refs are special and don't need to be passed to the dependency array.
  }, []);
  return {
    ref,
    onResetScroll,
  };
}
